import { Box, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';

import { tokens } from '../../../theme-next';

interface MultiDropzoneProps {
  onFilesAdded: (files: File[]) => void;
  disabled?: boolean;
  accept?: string;
}

/**
 * Dropzone unique multi-fichiers. Accepte drag-and-drop + clic pour sélectionner.
 * Implémentation native (pas de dépendance à `react-dropzone`).
 */
export const MultiDropzone = ({
  onFilesAdded,
  disabled = false,
  accept = 'image/*,application/pdf',
}: MultiDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0 || disabled) return;
      const files = Array.from(fileList);
      onFilesAdded(files);
    },
    [onFilesAdded, disabled],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragActive) setIsDragActive(true);
  }, [isDragActive]);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  return (
    <Box
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={isDragActive ? 'sk-breathing-border' : ''}
      sx={{
        border: `2px dashed ${isDragActive ? tokens.color.yellow : tokens.color.border}`,
        backgroundColor: isDragActive ? tokens.color.yellowLight : tokens.color.white,
        borderRadius: `${tokens.radius['2xl']}px`,
        padding: '48px 24px',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color 180ms ease, background-color 180ms ease, transform 180ms ease',
        transform: isDragActive ? 'scale(1.01)' : 'scale(1)',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          borderColor: disabled ? tokens.color.border : tokens.color.yellow,
          backgroundColor: disabled ? tokens.color.white : tokens.color.yellowLight,
        },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={(event) => {
          handleFiles(event.target.files);
          // Permet de re-uploader le même fichier si besoin.
          event.target.value = '';
        }}
        style={{ display: 'none' }}
      />
      <Typography variant="h4" sx={{ color: tokens.color.dark, marginBottom: '6px' }}>
        Déposer tous les documents ici
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.color.textSecondary, marginBottom: '4px' }}>
        Carte grise, cerfa de cession, mandat, pièce d'identité, permis, assurance,
        contrôle technique, justificatif de domicile… l'IA classera chaque document.
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.color.textFaint }}>
        JPG · PNG · PDF · max 10 Mo par fichier
      </Typography>
    </Box>
  );
};
